import React, {useState, useCallback} from 'react';
import {useDropzone} from 'react-dropzone';
import {Mic, Upload, Copy, Loader2, AlertTriangle, FileAudio, Volume2} from 'lucide-react';

const SpeechToTextPage = () => {
    const [file, setFile] = useState(null);
    const [transcription, setTranscription] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    const onDrop = useCallback(acceptedFiles => {
        if (acceptedFiles.length > 0) {
            setFile(acceptedFiles[0]);
            // Clear previous results when a new file is selected
            setTranscription('');
            setError(null);
        }
    }, []);

    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        onDrop,
        accept: {
            'audio/*': ['.mp3', '.wav', '.m4a', '.aac', '.ogg']
        },
        maxFiles: 1
    });

    const handleTranscribe = async () => {
        if (!file) {
            setError('Please select an audio file first');
            return;
        }

        setIsLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch('https://api.digitalaalim.com/speech_to_text/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Transcription failed with status: ${response.status}`);
            }

            const result = await response.json();
            setTranscription(result.text);
        } catch (error) {
            console.error('Error during transcription:', error);
            setError(error.message || 'An error occurred during transcription');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyText = () => {
        navigator.clipboard.writeText(transcription)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
            });
    };

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <Mic className="text-blue-600" size={32}/>
                    Urdu Speech to Text
                </h1>
                <p className="mt-2 text-gray-600">
                    Transform Urdu Speech into text with advanced AI-powered transcription
                </p>
            </div>

            {/* Main content */}
            <div className="grid md:grid-cols-2 gap-6">
                {/* File upload section */}
                <div className="space-y-6">
                    <h2 className="text-lg font-semibold text-gray-800">Input File</h2>
                    <div {...getRootProps()} className={`
                            border-2 ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-dashed border-gray-300'} 
                            rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors
                            `}>

                        <input {...getInputProps()} />

                        <div className="flex flex-col items-center">
                            {isDragActive ? (
                                <>
                                    <Upload className="text-blue-500 mb-3" size={48}/>
                                    <p className="text-blue-600 font-medium">Drop the audio file here</p>
                                </>
                            ) : (
                                <>
                                    <FileAudio className="text-gray-400 mb-3" size={48}/>
                                    <p className="text-gray-600 font-medium">Drag & drop an audio file here</p>
                                    <p className="text-gray-500 text-sm mt-2">or click to select a file</p>
                                </>
                            )}
                            <p className="text-xs text-gray-500 mt-4">
                                Supported formats: MP3, WAV, M4A, AAC, OGG
                            </p>
                        </div>
                    </div>

                    {file && (
                        <div className="p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-start">
                                <Volume2 className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" size={18}/>
                                <div>
                                    <p className="font-medium text-gray-700">{file.name}</p>
                                    <p className="text-sm text-gray-500">
                                        {(file.size / (1024 * 1024)).toFixed(2)} MB • {file.type}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={handleTranscribe}
                        disabled={isLoading || !file}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md font-medium transition-colors ${
                            isLoading || !file
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="animate-spin" size={18}/>
                                Transcribing...
                            </>
                        ) : (
                            <>
                                <Mic size={18}/>
                                Transcribe Audio
                            </>
                        )}
                    </button>
                </div>

                {/* Transcription result section */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold text-gray-800">Transcribed Text</h2>

                        {transcription && (
                            <button
                                onClick={handleCopyText}
                                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                                <Copy size={14}/>
                                {copied ? 'Copied!' : 'Copy Text'}
                            </button>
                        )}
                    </div>

                    {error && (
                        <div className="flex items-center p-3 bg-red-50 text-red-700 rounded-md">
                            <AlertTriangle size={16} className="mr-2 flex-shrink-0"/>
                            <p className="text-sm">{error}</p>
                        </div>
                    )}

                    <div
                        className={`w-full h-80 p-4 border border-gray-300 rounded-lg overflow-auto ${
                            transcription ? 'font-urdu' : 'text-gray-400'
                        }`}
                        style={{
                            fontFamily: transcription ? 'Jameel Noori Nastaleeq, Calibri, sans-serif' : 'inherit',
                            fontSize: transcription ? '1.1rem' : 'inherit',
                            direction: 'rtl'
                        }}
                    >
                        {transcription}
                    </div>

                    {transcription && (
                        <div className="flex items-center">
                            <div className="flex-1 h-px bg-gray-200"></div>
                            <p className="px-3 text-xs text-gray-500">Urdu Transcription</p>
                            <div className="flex-1 h-px bg-gray-200"></div>
                        </div>
                    )}

                    {!transcription && !isLoading && file && (
                        <div className="text-center p-4">
                            <p className="text-sm text-gray-600">
                                Click "Transcribe Audio" to convert your audio to text
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SpeechToTextPage;